<template>
  <div class="atom-dropdown">
    <button
      class="atom-dropdown__current"
      :class="{ 'atom-dropdown__current--active': isOpenMenu }"
      :disabled="isDisabled"
      @click="handleCurrentClicked"
    >
      <span :class="{ 'atom-dropdown__placeholder': selectedOptions.length === 0 }">{{
        buttonLabel
      }}</span>
      <span class="atom-dropdown__icon">
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g opacity="0.5">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M1.93945 6L7.76268 11.8232C8.44609 12.5066 9.55413 12.5066 10.2375 11.8232L16.0608 6H13.9395L9.17689 10.7626C9.07926 10.8602 8.92097 10.8602 8.82334 10.7626L4.06077 6H1.93945Z"
              fill="black"
            />
          </g>
        </svg>
      </span>
    </button>
    <div
      v-if="isOpenMenu"
      class="atom-dropdown__menu"
      :style="{ 'max-height': `${maxHeight}` }"
      ref="menu"
    >
      <ul class="atom-dropdown__menu-list">
        <li v-for="item in menuList" :key="item.id" class="atom-dropdown__menu-item">
          <button
            class="atom-dropdown__option"
            :class="{
              'atom-dropdown__option--selected': isSelected(item),
              'atom-dropdown__option-no-checkmark': !hasCheck,
            }"
            @click="handleOptionClicked(item)"
          >
            <span class="atom-dropdown__option-check" v-if="selectedOptions.includes(item)">
              <calendar-check-icon />
            </span>
            <span>{{ item.name }}</span>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, PropType, ref, watch } from '@vue/composition-api'
import CalendarCheckIcon from '@/components/LinkPage/CalendarPane/parts/icons/CalendarCheckIcon.vue'

export type DropDownDataType = {
  id: number | string
  name: string
}

export default defineComponent({
  name: 'AtomDropdown',
  components: { CalendarCheckIcon },
  props: {
    placeholder: {
      type: String,
      default: '',
    },
    selectedOptions: {
      type: Array as PropType<DropDownDataType[]>,
      default: () => [],
    },
    menuList: {
      type: Array as PropType<DropDownDataType[]>,
      required: true,
    },
    isOpenMenu: {
      type: Boolean,
      default: false,
    },
    maxHeight: {
      type: String,
      default: 'none',
    },
    hasCheck: {
      type: Boolean,
      default: true,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    menuPosition: {
      type: Number,
      default: 0,
    },
  },
  setup(props, { emit }) {
    const menu = ref<HTMLElement | null>(null)
    const offsetPositionY = ref<number>(props.menuPosition)
    const buttonLabel = computed(() => {
      if (props.selectedOptions?.length > 0) {
        return props.selectedOptions.map((option) => option.name).join(',')
      }
      return props.placeholder
    })

    /**
     * 選択中かどうか
     */
    const isSelected = (item: DropDownDataType) =>
      props.selectedOptions.some((option) => option.id === item.id && option.name === item.name)

    const handleCurrentClicked = () => {
      emit('onClickCurrent')
    }
    const handleOptionClicked = (option: DropDownDataType) => {
      emit('onClickOption', option)
    }

    watch(
      () => props.isOpenMenu,
      (newVal) => {
        if (newVal) {
          nextTick(() => {
            const targetElm = document.querySelectorAll('.atom-dropdown__option--selected')[0]
            const menuPositionY = menu.value?.getBoundingClientRect().top || 0
            if (targetElm) {
              offsetPositionY.value = targetElm.getBoundingClientRect().top - menuPositionY
            }
            menu.value?.scrollTo(0, offsetPositionY.value)
          })
        }
      },
    )

    return {
      menu,
      buttonLabel,
      isSelected,
      handleCurrentClicked,
      handleOptionClicked,
    }
  },
})
</script>

<style scoped lang="scss">
.atom-dropdown {
  position: relative;

  &__current {
    position: relative;
    display: block;
    width: 100%;
    height: 36px;
    padding: 8px 30px 8px 12px;
    overflow: hidden;
    font-size: 13px;
    font-weight: 500;
    color: $gray75;
    white-space: nowrap;
    text-align: left;
    text-overflow: ellipsis;
    background-color: $white;
    border: 1px solid $gray10;
    border-radius: 4px;

    &--active {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;

      .atom-dropdown__icon {
        transform: translateY(-50%) rotate(180deg);
      }
    }
  }

  &__placeholder {
    color: $black50;
  }

  &__icon {
    position: absolute;
    top: 50%;
    right: 8px;
    transform: translateY(-50%);
  }

  &__menu {
    position: absolute;
    top: 35px;
    left: 0;
    z-index: 1;
    width: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior-y: contain;
    background-color: $white;
    border: 1px solid $gray10;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;

    &-item {
      + .atom-dropdown__menu-item {
        position: relative;

        &::after {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 0;
          content: '';
          border-top: 1px solid $black10;
          pointer-events: none;
        }
      }
    }
  }

  &__option {
    position: relative;
    display: block;
    width: 100%;
    height: 36px;
    padding: 8px 7px 8px 34px;
    overflow: hidden;
    font-size: 13px;
    font-weight: 500;
    color: $gray75;
    white-space: nowrap;
    text-align: left;
    text-overflow: ellipsis;
    letter-spacing: 0.04em;
    background-color: $white;

    &.atom-dropdown__option-no-checkmark {
      padding-left: 12px;
    }

    &--selected {
      color: $black;
      background-color: $green;
    }

    &-check {
      position: absolute;
      top: 50%;
      left: 12px;
      width: 18px;
      height: 18px;
      color: $black;
      transform: translateY(-50%);

      .atom-dropdown__option-no-checkmark & {
        display: none;
      }
    }
  }
}
</style>
